const silverDetails = [
  {
    icon: 'ri-graduation-cap-line',
    title: 'General Host Training',
    description: 'Comprehensive training covering all aspects of successful Airbnb hosting, from guest communication to platform best practices.'
  },
  {
    icon: 'ri-home-4-line',
    title: 'Property Evaluation',
    description: 'Detailed assessment of your property\'s potential, identifying strengths and areas for improvement to maximize bookings.'
  },
  {
    icon: 'ri-file-text-line',
    title: 'Codes & Guidelines',
    description: 'In-depth discussion of local building codes, zoning regulations, and compliance requirements specific to your area.'
  },
  {
    icon: 'ri-tools-line',
    title: 'Contractor Guidance',
    description: 'Learn how to find, vet, and work with local contractors for any necessary renovations or compliance work.'
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Permit Application Help',
    description: 'Step-by-step guidance on understanding and completing permit applications for short-term rental operations.'
  },
  {
    icon: 'ri-paint-brush-line',
    title: 'Space Design Consultation',
    description: 'Expert advice on designing and staging your space to appeal to guests and stand out in listings.'
  },
  {
    icon: 'ri-airbnb-line',
    title: 'Airbnb Setup',
    description: 'Assistance with creating your Airbnb listing, including photos, descriptions, and initial pricing strategy.'
  },
  {
    icon: 'ri-chat-3-line',
    title: 'Unlimited Chat Support',
    description: 'Round-the-clock access to our team via online chat for quick questions and ongoing guidance.'
  }
];

const goldExtras = [
  {
    icon: 'ri-shield-check-line',
    title: 'Permit Application Assistance',
    description: 'We handle the permit application process for you, ensuring all documentation is complete and submitted correctly.'
  },
  {
    icon: 'ri-team-line',
    title: 'Contractor Sourcing',
    description: 'We find, vet, and recommend qualified contractors from our trusted network, saving you time and ensuring quality.'
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Price Evaluation & Negotiation',
    description: 'Expert negotiation with contractors and vendors to ensure you get the best value for your renovation budget.'
  },
  {
    icon: 'ri-layout-line',
    title: 'Complete Space Setup',
    description: 'Full coordination of your space setup, from initial design through final staging and photography.'
  },
  {
    icon: 'ri-shopping-bag-3-line',
    title: 'Furniture Sourcing',
    description: 'Professional furniture selection and sourcing that balances style, durability, and budget considerations.'
  },
  {
    icon: 'ri-global-line',
    title: 'Multi-Platform Listing',
    description: 'Setup on Airbnb, VRBO, Booking.com, and other platforms to maximize your property\'s exposure and bookings.'
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Dynamic Pricing Setup',
    description: 'Implementation of smart pricing strategies that adjust based on demand, seasonality, and local events.'
  },
  {
    icon: 'ri-star-line',
    title: 'Listing Optimization',
    description: 'Professional optimization of all listing elements to rank higher in search results and convert more bookings.'
  }
];

const platinumExtras = [
  {
    icon: 'ri-search-eye-line',
    title: 'Detailed Space Evaluation',
    description: 'Comprehensive analysis of your property with professional recommendations for maximizing rental potential and guest appeal.'
  },
  {
    icon: 'ri-pencil-ruler-2-line',
    title: 'Professional Space Design',
    description: 'Complete interior design service creating a cohesive, guest-friendly space that stands out in the marketplace.'
  },
  {
    icon: 'ri-file-shield-2-line',
    title: 'Complete Permit Handling',
    description: 'We manage the entire permit application process from start to finish, ensuring full compliance with local regulations.'
  },
  {
    icon: 'ri-user-settings-line',
    title: 'Contractor Hiring & Management',
    description: 'We hire and manage contractors on your behalf, handling all communications, scheduling, and quality control.'
  },
  {
    icon: 'ri-eye-line',
    title: 'Full Project Supervision',
    description: 'Dedicated project manager overseeing every aspect of your property setup, keeping you informed every step of the way.'
  },
  {
    icon: 'ri-shopping-cart-2-line',
    title: 'Furniture Purchasing Service',
    description: 'We handle all furniture purchasing, delivery coordination, and setup to create a turnkey ready property.'
  },
  {
    icon: 'ri-rocket-line',
    title: 'Complete Airbnb Launch',
    description: 'Full-service listing creation, professional photography, and strategic launch to maximize initial bookings.'
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Algorithm-Optimized Pricing',
    description: 'Advanced pricing strategy leveraging platform algorithms to maximize your revenue and booking rate.'
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Unlimited Phone Access',
    description: 'Direct access to your dedicated specialist for 3 months with unlimited phone support whenever you need it.'
  }
];

export default function ServiceDetails() {
  return (
    <section id="details" className="py-24 bg-gradient-to-b from-white to-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Silver Details */}
        <div className="mb-24">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-gray-200 text-[#1A2332] text-sm font-semibold rounded-full mb-4">
              Silver Package
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Everything You Need to Get Started
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our Silver package provides comprehensive guidance and training to help you successfully launch your Airbnb hosting business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {silverDetails.map((detail, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 group cursor-pointer"
              >
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FF6B6B] transition-colors">
                  <i className={`${detail.icon} text-2xl text-[#1A2332] group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#1A2332] mb-3">
                  {detail.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {detail.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Gold Extras */}
        <div className="mb-24">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#FF6B6B] text-white text-sm font-semibold rounded-full mb-4">
              Gold Package Extras
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Full-Service, Hands-On Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Gold package includes everything in Silver, plus these additional services where we do the heavy lifting for you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {goldExtras.map((extra, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 group cursor-pointer border-2 border-[#FF6B6B]/20"
              >
                <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FF6B6B] transition-colors">
                  <i className={`${extra.icon} text-2xl text-[#FF6B6B] group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#1A2332] mb-3">
                  {extra.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {extra.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Platinum Extras */}
        <div>
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#1A2332] text-white text-sm font-semibold rounded-full mb-4">
              Platinum Package Extras
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Complete Turnkey Solution
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Platinum package includes everything in Gold, plus premium white-glove service where we handle everything from start to finish.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platinumExtras.map((extra, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 group cursor-pointer border-2 border-[#1A2332]/30"
              >
                <div className="w-14 h-14 bg-[#1A2332]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#1A2332] transition-colors">
                  <i className={`${extra.icon} text-2xl text-[#1A2332] group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#1A2332] mb-3">
                  {extra.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {extra.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
